import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";
import { Spinner } from "./Spinner";

const buttonVariants = cva(
  // Base styles - using direct values for maximum compatibility
  "inline-flex items-center justify-center whitespace-nowrap rounded-2xl font-medium transition-colors duration-200 focus-visible:outline-none disabled:pointer-events-none gap-2",
  {
    variants: {
      variant: {
        // PRIMARY VARIANT - MEBI Yellow
        primary: [
          // Default state
          "bg-mebi-yellow-500 text-white",
          // Hover state
          "hover:bg-mebi-yellow-600",
          // Active state
          "active:bg-mebi-yellow-700",
          // Focus state - 4px inside border
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-mebi-yellow-700",
          // Disabled state
          "disabled:bg-mebi-yellow-300 disabled:text-white disabled:cursor-not-allowed",
        ],

        // SECONDARY VARIANT - MEBI Yellow Outline
        secondary: [
          // Default state - background, text, 1px border
          "bg-mebi-yellow-100 text-mebi-yellow-500 border border-mebi-yellow-500",
          // Hover state
          "hover:bg-mebi-yellow-200 hover:text-mebi-yellow-500 hover:border-mebi-yellow-500",
          // Active state
          "active:bg-mebi-yellow-200 active:text-mebi-yellow-700 active:border-mebi-yellow-700",
          // Focus state - 4px inside border
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-mebi-yellow-500",
          // Disabled state
          "disabled:bg-mebi-yellow-100 disabled:text-mebi-yellow-300 disabled:border-mebi-yellow-300 disabled:cursor-not-allowed",
        ],

        // TERTIARY VARIANT - Neutral
        tertiary: [
          // Default state
          "bg-neutral-200 text-neutral-1200 border border-neutral-400",
          // Hover state
          "hover:bg-neutral-300 hover:text-neutral-1200 hover:border-neutral-500",
          // Active state
          "active:bg-neutral-400 active:text-neutral-1200 active:border-neutral-600",
          // Focus state - 4px inside border
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-neutral-500",
          // Disabled state
          "disabled:bg-neutral-200 disabled:text-neutral-600 disabled:border-neutral-400 disabled:cursor-not-allowed",
        ],

        // DESTRUCTIVE VARIANT - MEBI Red
        destructive: [
          // Default state
          "bg-mebi-red-500 text-white",
          // Hover state
          "hover:bg-mebi-red-600",
          // Active state
          "active:bg-mebi-red-700",
          // Focus state
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-mebi-red-500",
          // Disabled state
          "disabled:bg-mebi-red-300 disabled:text-white disabled:cursor-not-allowed",
        ],

        // GHOST VARIANT - Transparent
        ghost: [
          // Default state
          "bg-transparent text-neutral-1200 hover:bg-neutral-200",
          // Hover state
          "hover:text-neutral-1200",
          // Active state
          "active:bg-neutral-300 active:text-neutral-1200",
          // Focus state
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-neutral-400",
          // Disabled state
          "disabled:bg-transparent disabled:text-neutral-600 disabled:cursor-not-allowed",
        ],
      },
      size: {
        // MEBI sizes with direct px values
        sm: "h-8 px-3 text-sm font-semibold min-w-[2rem]",    // 32px height, 12px padding, 14px text
        md: "h-10 px-4 text-sm font-semibold min-w-[2.5rem]",  // 40px height, 16px padding, 14px text
        lg: "h-12 px-6 text-base font-semibold min-w-[3rem]", // 48px height, 24px padding, 16px text
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
  /** Show loading spinner and disable button */
  loading?: boolean;
  /** Icon to show on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to show on the right side */
  rightIcon?: React.ReactNode;
}

/**
 * Button component following MEBI Design System
 * 
 * @example
 * ```tsx
 * <Button variant="primary" size="md">
 *   Primary Button
 * </Button>
 * 
 * <Button variant="secondary" size="lg" loading>
 *   Loading Button
 * </Button>
 * 
 * <Button variant="tertiary" leftIcon={<PlusIcon />}>
 *   With Icon
 * </Button>
 * ```
 */

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = loading || disabled;

    // Get spinner color based on variant and state
    const getSpinnerColor = () => {
      if (loading) {
        switch (variant) {
          case "primary":
          case "destructive":
            return "white"; // White spinner on colored background
          case "secondary":
            return "mebi-yellow-700"; // Dark yellow spinner on light background
          case "tertiary":
          case "ghost":
            return "neutral-1200"; // Dark spinner on neutral background
          default:
            return "white";
        }
      }
      return "white";
    };

    // Get spinner size based on button size
    const getSpinnerSize = () => {
      switch (size) {
        case "sm":
          return "sm";
        case "lg":
          return "lg";
        default:
          return "md";
      }
    };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {/* Loading state: show spinner, hide text and icons */}
        {loading ? (
          <Spinner
            size={getSpinnerSize()}
            variant={
              getSpinnerColor() === "white"
                ? "white"
                : getSpinnerColor() === "mebi-yellow-700"
                  ? "primary"
                  : "neutral"
            }
          />
        ) : (
          <>
            {/* Left icon - optional */}
            {leftIcon && (
              <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">{leftIcon}</span>
            )}
            {/* Button text */}
            {children && <span className="truncate">{children}</span>}
            {/* Right icon - optional */}
            {rightIcon && (
              <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">{rightIcon}</span>
            )}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export default Button;
export { Button };
